
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Search, Check } from "lucide-react"

interface IconPickerProps {
  value: string
  onChange: (icon: string) => void
}

const iconCategories = {
  money: ['💰', '💵', '💴', '💶', '💷', '💳', '🏦', '💸', '💲', '📈', '📉', '💹', '🪙', '🎁', '💼'],
  food: ['🍔', '🍕', '🍝', '🍜', '🍲', '🥗', '🍱', '🍙', '🍛', '🍤', '🍣', '🥡', '🥘', '🍖', '☕', '🍺', '🍷', '🛒', '🍽️'],
  transport: ['🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🛵', '🚲', '🛴', '✈️', '🚁', '⛽', '🚦'],
  shopping: ['🛍️', '🛒', '👕', '👔', '👗', '👠', '👟', '👜', '💄', '💍', '🎀', '🧥', '👘', '🕶️'],
  entertainment: ['🎬', '🎮', '🎯', '🎲', '🎰', '🎪', '🎨', '🎭', '🎪', '🎸', '🎹', '🎤', '🎧', '📺', '📻', '🎵'],
  health: ['💪', '🏋️', '🏃', '🚴', '🏊', '⚽', '🏀', '🏈', '⚾', '🎾', '🏥', '💊', '💉', '🩺', '🧘'],
  home: ['🏠', '🏡', '🏘️', '🏢', '🏬', '🏭', '🛏️', '🚪', '🪟', '💡', '🔌', '🔋', '📱', '📞', '🌐'],
  education: ['📚', '📖', '📝', '📕', '📗', '📘', '📙', '🎓', '🖊️', '✏️', '📐', '🖍️', '📎', '📌'],
  other: ['📦', '📌', '🎯', '⭐', '✨', '🔔', '🎈', '🎉', '🎊', '🔑', '🔒', '🔓', '⚙️', '🔧', '🔨'],
}

export function IconPicker({ value, onChange }: IconPickerProps) {
  const [search, setSearch] = useState("")
  const [activeCategory, setActiveCategory] = useState("money")

  // Get all icons for search
  const allIcons = Object.values(iconCategories).flat()
  const filteredAllIcons = search ? allIcons.filter(() => true) : []

  // Filter icons based on category
  const getFilteredIcons = (category: string) => {
    const icons = iconCategories[category as keyof typeof iconCategories] || []
    return icons
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search icons..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 border-white/20 h-11 transition-all focus:border-primary/50 focus:ring-2 focus:ring-primary/20"
        />
      </div>

      {search ? (
        // Show all matching icons when searching
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">All Icons</Label>
          <div className="grid grid-cols-6 gap-2 max-h-64 overflow-y-auto overflow-hidden p-3 rounded-xl bg-background/20 border border-white/10">
            {filteredAllIcons.map((icon) => (
              <Button
                key={icon}
                variant="ghost"
                size="sm"
                className={`w-full aspect-square text-2xl relative group transition-all duration-200 hover:scale-105 ${
                  value === icon
                    ? 'bg-primary/20 border-2 border-primary shadow-lg scale-105'
                    : 'hover:bg-white/10 border-2 border-transparent'
                }`}
                onClick={() => onChange(icon)}
              >
                {icon}
              </Button>
            ))}
          </div>
        </div>
      ) : (
        // Show categorized icons
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
          <TabsList className="w-full flex overflow-x-auto justify-start gap-2 bg-background/20 p-2 h-auto">
            <TabsTrigger
              value="money"
              className="flex-shrink-0 text-xl data-[state=active]:bg-primary/20 data-[state=active]:shadow-md transition-all px-4"
              title="Money"
            >
              💰
            </TabsTrigger>
            <TabsTrigger
              value="food"
              className="flex-shrink-0 text-xl data-[state=active]:bg-primary/20 data-[state=active]:shadow-md transition-all px-4"
              title="Food & Drink"
            >
              🍔
            </TabsTrigger>
            <TabsTrigger
              value="transport"
              className="flex-shrink-0 text-xl data-[state=active]:bg-primary/20 data-[state=active]:shadow-md transition-all px-4"
              title="Transport"
            >
              🚗
            </TabsTrigger>
            <TabsTrigger
              value="shopping"
              className="flex-shrink-0 text-xl data-[state=active]:bg-primary/20 data-[state=active]:shadow-md transition-all px-4"
              title="Shopping"
            >
              🛍️
            </TabsTrigger>
            <TabsTrigger
              value="entertainment"
              className="flex-shrink-0 text-xl data-[state=active]:bg-primary/20 data-[state=active]:shadow-md transition-all px-4"
              title="Entertainment"
            >
              🎬
            </TabsTrigger>
            <TabsTrigger
              value="health"
              className="flex-shrink-0 text-xl data-[state=active]:bg-primary/20 data-[state=active]:shadow-md transition-all px-4"
              title="Health & Sports"
            >
              💪
            </TabsTrigger>
            <TabsTrigger
              value="home"
              className="flex-shrink-0 text-xl data-[state=active]:bg-primary/20 data-[state=active]:shadow-md transition-all px-4"
              title="Home & Tech"
            >
              🏠
            </TabsTrigger>
            <TabsTrigger
              value="education"
              className="flex-shrink-0 text-xl data-[state=active]:bg-primary/20 data-[state=active]:shadow-md transition-all px-4"
              title="Education"
            >
              📚
            </TabsTrigger>
            <TabsTrigger
              value="other"
              className="flex-shrink-0 text-xl data-[state=active]:bg-primary/20 data-[state=active]:shadow-md transition-all px-4"
              title="Other"
            >
              📦
            </TabsTrigger>
          </TabsList>

          {Object.keys(iconCategories).map((category) => (
            <TabsContent key={category} value={category} className="mt-4">
              <div className="grid grid-cols-6 gap-2 max-h-64 overflow-y-auto overflow-hidden p-3 rounded-xl bg-background/20 border border-white/10">
                {getFilteredIcons(category).map((icon) => (
                  <Button
                    key={icon}
                    variant="ghost"
                    size="sm"
                    className={`w-full aspect-square text-2xl relative group transition-all duration-200 hover:scale-105 ${
                      value === icon
                        ? 'bg-primary/20 border-2 border-primary shadow-lg scale-105'
                        : 'hover:bg-white/10 border-2 border-transparent'
                    }`}
                    onClick={() => onChange(icon)}
                  >
                    {icon}
                  </Button>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  )
}
